/*
 * OIDO warranty registration form logic.
 *
 * Talks to erp-api (Baserow-backed), replacing the deprecated Airtable POST.
 *   - POST {apiBase}/v1/warranty/register  (JSON)
 *
 * Contract mirrors apps/erp-api/src/routes/warranty.ts:
 *   register body: firstName?, lastName?, email?, phone?, street1, street2?, city,
 *     state, zip, deviceSelection (HearOne|HearDuo|HearMe), productId, warrantyType
 *     (Limited 1Year|OIDO Care|OIDO Care+), purchaseDate (YYYY-MM-DD),
 *     purchasedThroughAmazon (boolean)
 *   identity: EITHER a valid email OR (firstName AND lastName) must be present
 *     (both is fine). Enforced client-side below via setCustomValidity; empty
 *     identity fields are omitted from the payload entirely.
 *   201 -> { warrantyId, warrantyNumber, status: 'registered',
 *     requiresReview: false, endDate }
 *   400 with code 'invalid_product_id' -> the identifier could not be verified
 *     (malformed, unknown, or registered to another owner; the server never
 *     says which). Shown via ERROR_COPY below.
 *   400 without a code -> legacy generic validation failure.
 *   429 -> rate limited (10 req / 10 min per IP)
 *
 * This form is Amazon-first: proof-of-purchase upload has been removed, and a
 * structured ship-to address is required (a warranty claim ships a replacement).
 *
 * CLINIC MODE contract differs (routes/partner/clinic-registration.ts): the patient is at the
 * counter, so firstName AND lastName are required, email OR phone (one or both) is the contact,
 * and the address is optional but all or nothing (street1, city, state, zip together, or none).
 *
 * No auth/CORS config needed: the public routes are open + rate-limited.
 *
 * SCAN QR. The product-ID QR on the box sticker encodes "Model:HearDuo20250705-36".
 * interpretProductId() (rules ported from apps/internal-dashboard/lib/scan-format.ts)
 * turns that into the model-scoped "HearDuo-20250705-36" and selects the Device: a bare
 * serial repeats across models, and the scoped id is what names exactly one unit. The
 * same cleanup runs on a typed or pasted value, so a HID scanner or a paste of the raw
 * QR text lands the same way. qr-scanner is fetched only when someone taps Scan QR.
 *
 * CLINIC MODE (data-clinic-slug on the section; templates/page.<slug>-registration.json):
 *   GET  {apiBase}/v1/partner/clinics/<slug>/registration      -> { clinicName, locations,
 *     plans: [{ value, label }], token? }
 *   POST {apiBase}/v1/partner/clinics/<slug>/warranty/register -> as the public route, plus
 *     a `location` from that list; 201 may carry a renewed { token, expiresAt }.
 *   Both need `Authorization: Bearer <token>`, the clinic's DEVICE token, minted by
 *   POST .../session { password } and kept in localStorage under the same key as the
 *   clinic's assistant page (assets/clinic-assistant.js), so one sign-in covers both.
 *   No rate limit on these routes; the token is the gate.
 */
(function () {
  'use strict';

  // ---- Product ID interpretation --------------------------------------------------------

  // "Model:" label, including the factory's "ModeI:" typo (capital I for the l).
  var MODEL_LABEL = /^mode[li]:\s*/i;
  // A model name glued to the serial ("HearDuo20250705-36") or written scoped
  // ("HearDuo-20250705-36"). In typed text only before a digit, so a word that merely
  // starts with a model name is left alone; after the QR's "Model:" label, whatever
  // follows is the serial.
  var MODEL_NAME = /^(hearone|hearduo|hearme)\s*-?\s*(?=\d)/i;
  var MODEL_NAME_AFTER_LABEL = /^(hearone|hearduo|hearme)\s*-?\s*/i;
  // The per-device UDI QR: "(21)DG030043". The server stores the bare code.
  var UDI_AI_21 = /^\(21\)/i;
  var MODELS = ['HearOne', 'HearDuo', 'HearMe'];

  function canonicalModel(name) {
    var lower = String(name).toLowerCase();
    for (var i = 0; i < MODELS.length; i++) {
      if (MODELS[i].toLowerCase() === lower) return MODELS[i];
    }
    return null;
  }

  /**
   * Raw scan or typed text -> { productId, model }. Anything not recognized (a bare
   * serial, a GS1 UDI typed off the box) passes through unchanged: the server is the
   * gate, and it matches UDIs raw or punctuation-stripped.
   */
  function interpretProductId(raw) {
    var v = String(raw || '')
      .replace(/[​-‍﻿]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (UDI_AI_21.test(v)) {
      var udi = (v.replace(UDI_AI_21, '').split('(')[0] || '').trim();
      return { productId: udi, model: null };
    }
    var labelled = MODEL_LABEL.test(v);
    v = v.replace(MODEL_LABEL, '');
    var m = (labelled ? MODEL_NAME_AFTER_LABEL : MODEL_NAME).exec(v);
    if (m) {
      var model = canonicalModel(m[1]);
      var serial = v.slice(m[0].length).trim();
      if (model && serial) return { productId: model + '-' + serial, model: model };
    }
    return { productId: v, model: null };
  }

  // Product ID prefilter, mirroring erp-api isPlausibleProductId
  // (services/unit.ts): broad character allowlist + must contain a digit.
  var productIdPattern = /^[0-9A-Za-z()\-_./+ ]{4,100}$/;

  function isPlausibleProductId(v) {
    return productIdPattern.test(v) && /[0-9]/.test(v);
  }

  // qr-scanner's legacy build sets window.QrScanner. One load per page, retried after a failure.
  var qrLibPromise = null;
  function loadQrLib(url) {
    if (window.QrScanner) return Promise.resolve(window.QrScanner);
    if (qrLibPromise) return qrLibPromise;
    qrLibPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () {
        if (window.QrScanner) resolve(window.QrScanner);
        else reject(new Error('scanner_missing'));
      };
      s.onerror = function () {
        qrLibPromise = null;
        reject(new Error('scanner_load_failed'));
      };
      document.head.appendChild(s);
    });
    return qrLibPromise;
  }

  // fetch() rejects with a TypeError when the request never completes, and its message
  // differs by browser ("Failed to fetch", Safari's "Load failed"), so test the type.
  function isNetworkError(err) {
    return err instanceof TypeError;
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      if (value) window.localStorage.setItem(key, value);
      else window.localStorage.removeItem(key);
    } catch (e) {
      // Private mode / storage disabled: works for this page view only.
    }
  }

  // Camera permission is the browser's to remember, never the page's. What the page can
  // tell is that this device granted the camera before and the browser has since forgotten
  // (back to 'prompt'), which is "it asks every time"; the tip then names the one-time
  // device setting. Same logic as internal-dashboard/lib/camera-permission.ts.
  var CAMERA_GRANTED_KEY = 'oido-camera-granted';

  function cameraPermissionForgotten() {
    if (readStorage(CAMERA_GRANTED_KEY) !== '1') return Promise.resolve(false);
    try {
      if (!navigator.permissions || !navigator.permissions.query) return Promise.resolve(false);
      return navigator.permissions.query({ name: 'camera' }).then(
        function (status) {
          return status.state === 'prompt';
        },
        function () {
          return false;
        }
      );
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  function cameraRememberTip() {
    var ua = navigator.userAgent;
    var ios =
      /iPhone|iPad|iPod/.test(ua) || (ua.indexOf('Macintosh') !== -1 && navigator.maxTouchPoints > 1);
    var standalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      navigator.standalone === true;
    if (ios && standalone) {
      return (
        "Asked for the camera every time? iPhone home-screen apps can't keep it. Open this page " +
        'in Safari and set Website Settings > Camera > Allow from the page menu in the address ' +
        'bar, or allow the camera for all sites in Settings > Apps > Safari > Camera.'
      );
    }
    if (ios) {
      return (
        'Asked for the camera every time? Tap the page menu in the address bar (aA), then ' +
        'Website Settings > Camera > Allow.'
      );
    }
    return (
      'Asked for the camera every time? Tap the icon left of the web address, then ' +
      'Permissions or Site settings > Camera > Allow.'
    );
  }

  function init(root) {
    var form = root.querySelector('[data-oido-warranty-form]');
    if (!form) return;

    var apiBase = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
    // Served by `shopify theme dev` (npm run dev:storefront, 127.0.0.1:9292), the form talks to
    // the dev erp-api from `npm run dev:erp-api:local` (port 3210), never to production, the
    // same rule as the clinic assistant page. The live page is served from oidohear.com, so
    // this never applies there.
    if (/^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)) apiBase = 'http://localhost:3210';

    var slug = (root.getAttribute('data-clinic-slug') || '').trim();
    var clinicMode = slug !== '';
    var clinicPath = '/v1/partner/clinics/' + encodeURIComponent(slug);
    // The SAME key as assets/clinic-assistant.js: a device signed in on either page is
    // signed in on both.
    var TOKEN_KEY = 'oido-clinic-' + slug + '-token';
    // The store this device last registered from. A per-device convenience: the counter
    // iPad lives at one store, and the choice stays visible and required on every sale.
    var STORE_KEY = 'oido-clinic-' + slug + '-store';
    var token = clinicMode ? readStorage(TOKEN_KEY) : null;

    var statusEl = form.querySelector('[data-oido-status]');
    var submitBtn = form.querySelector('[data-oido-submit]');
    var addressDetails = form.querySelector('[data-oido-address]');
    var addressFieldNames = ['street1', 'street2', 'city', 'state', 'zip'];
    var productIdEl = form.querySelector('[name="productId"]');
    var deviceEl = form.querySelector('[name="deviceSelection"]');
    var locationEl = form.querySelector('[data-oido-locations]');
    var plansEl = form.querySelector('[data-oido-plans]');

    // A clinic page runs on a shared counter device: the browser must not offer one
    // customer's name, email or address to the next.
    if (clinicMode) {
      Array.prototype.forEach.call(form.querySelectorAll('input'), function (el) {
        el.setAttribute('autocomplete', 'off');
      });
    }

    function openAddress() {
      if (addressDetails && !addressDetails.open) addressDetails.open = true;
    }

    // Keep the collapsed address block from ever hiding data or validation errors:
    // open it if the browser autofilled a value, if a field inside is focused, and
    // (below, in submit) if a required address field is invalid.
    if (addressDetails) {
      var anyPrefilled = addressFieldNames.some(function (n) {
        return getValue(n) !== '';
      });
      if (anyPrefilled) openAddress();
      addressFieldNames.forEach(function (n) {
        var el = form.querySelector('[name="' + n + '"]');
        if (el) el.addEventListener('focus', openAddress);
      });
    }

    function setStatus(message, kind) {
      if (!statusEl) return;
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.setAttribute('data-kind', kind || 'info');
    }

    function clearStatus() {
      if (!statusEl) return;
      statusEl.hidden = true;
      statusEl.textContent = '';
      statusEl.removeAttribute('data-kind');
    }

    function setBusy(busy) {
      if (!submitBtn) return;
      submitBtn.disabled = busy;
      submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    // Identity either/or rule: the server accepts EITHER a valid email OR
    // (firstName AND lastName); both is fine. None of the three inputs carries a
    // native `required`, so the group is enforced here with setCustomValidity
    // (anchored on the email field) so form.reportValidity() still drives the
    // native bubble + focus. Re-checked on input after the first failed attempt.
    // In clinic mode the names carry a native `required`, so the group here is email-or-phone.
    var identityMessage = clinicMode
      ? "Enter the patient's email or phone number."
      : 'Enter your email, or your first and last name.';
    var addressMessage = 'Finish the shipping address (street, city, state and ZIP), or leave it all blank.';

    var productIdMessage =
      'Enter the serial number or UDI printed on your device or its packaging.';
    var productIdAttempted = false;

    function updateProductIdValidity() {
      if (!productIdEl) return true;
      productIdEl.setCustomValidity('');
      var ok = isPlausibleProductId(interpretProductId(productIdEl.value).productId);
      if (!ok) productIdEl.setCustomValidity(productIdMessage);
      return ok;
    }

    function flash(el) {
      if (!el) return;
      el.classList.add('is-scanned');
      window.setTimeout(function () {
        el.classList.remove('is-scanned');
      }, 1600);
    }

    // Writes an interpreted Product ID back into the form, and selects the Device when
    // the value named one, so the two can never disagree.
    function applyProductId(result, fromScan) {
      if (!productIdEl) return;
      productIdEl.value = result.productId;
      if (result.model && deviceEl) {
        deviceEl.value = result.model;
        if (fromScan) flash(deviceEl);
      }
      if (productIdAttempted) updateProductIdValidity();
      if (fromScan) flash(productIdEl);
    }

    if (productIdEl) {
      productIdEl.addEventListener('input', function () {
        if (!productIdAttempted) return;
        updateProductIdValidity();
      });
      // A pasted or HID-scanned "Model:HearDuo20250705-36" is cleaned the moment the
      // field is left, so the person sees exactly what will be submitted.
      productIdEl.addEventListener('change', function () {
        var r = interpretProductId(productIdEl.value);
        if (r.productId !== productIdEl.value || r.model) applyProductId(r, false);
      });
    }

    // ---- Scan QR ------------------------------------------------------------------------

    var scanBtn = form.querySelector('[data-oido-scan]');
    var scanPanel = form.querySelector('[data-oido-scan-panel]');
    var scanVideo = form.querySelector('[data-oido-scan-video]');
    var scanMsg = form.querySelector('[data-oido-scan-msg]');
    var scanStopBtn = form.querySelector('[data-oido-scan-stop]');
    var qrLibUrl = root.getAttribute('data-qr-lib') || '';
    var scanner = null;
    var scanning = false;

    // Camera needs a secure context (HTTPS or localhost) and getUserMedia. Without them
    // the button never appears, and the field works exactly as before.
    var cameraPossible = !!(
      scanBtn &&
      scanPanel &&
      scanVideo &&
      qrLibUrl &&
      window.isSecureContext &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );

    function setScanMsg(text, kind) {
      if (!scanMsg) return;
      scanMsg.textContent = text;
      scanMsg.setAttribute('data-kind', kind || 'info');
    }

    function releaseCamera() {
      if (!scanner) return;
      try {
        scanner.stop();
        scanner.destroy();
      } catch (e) {
        // Already torn down.
      }
      scanner = null;
    }

    function stopScan() {
      scanning = false;
      releaseCamera();
      if (scanPanel) scanPanel.hidden = true;
      if (scanBtn) scanBtn.setAttribute('aria-expanded', 'false');
    }

    function cameraErrorCopy(err) {
      var text = (
        typeof err === 'string' ? err : (err && (err.name || '') + ' ' + (err.message || '')) || ''
      ).toLowerCase();
      if (text.indexOf('scanner_') !== -1) {
        return 'The scanner did not load. Type the Product ID instead.';
      }
      if (text.indexOf('notallowed') !== -1 || text.indexOf('permission') !== -1) {
        return 'Camera permission was denied. Allow the camera for this site, or type the Product ID.';
      }
      if (text.indexOf('notfound') !== -1 || text.indexOf('not found') !== -1) {
        return 'No camera found on this device. Type the Product ID instead.';
      }
      return 'The camera is unavailable. Type the Product ID instead.';
    }

    function onDecode(result) {
      if (!scanning) return;
      var raw = result && typeof result === 'object' ? result.data : result;
      var r = interpretProductId(raw);
      // Any QR in view decodes, including a URL printed elsewhere on the box. Keep the
      // camera open on those instead of filling the field with something that is not an id.
      if (!isPlausibleProductId(r.productId)) {
        setScanMsg(
          'That QR code is not a Product ID. Scan the QR next to the Product ID number on the box sticker.',
          'error'
        );
        return;
      }
      stopScan();
      applyProductId(r, true);
      setStatus(
        'Scanned ' + r.productId + (r.model ? ' (' + r.model + ')' : '') + '.',
        'info'
      );
      if (scanBtn) scanBtn.focus();
    }

    function startScan() {
      if (scanning) return;
      scanning = true;
      scanPanel.hidden = false;
      scanBtn.setAttribute('aria-expanded', 'true');
      setScanMsg('Starting the camera...', 'info');

      loadQrLib(qrLibUrl)
        .then(function (QrScanner) {
          if (!scanning) return null;
          scanner = new QrScanner(scanVideo, onDecode, {
            preferredCamera: 'environment',
            maxScansPerSecond: 5,
            // Our own reticle is drawn in the section CSS.
            highlightScanRegion: false,
            highlightCodeOutline: false,
            returnDetailedScanResult: true,
          });
          return scanner.start().then(function () {
            writeStorage(CAMERA_GRANTED_KEY, '1');
            // Stopped while the camera was starting: let go of it again.
            if (!scanning) releaseCamera();
            else setScanMsg('Point the camera at the QR code on the box sticker.', 'info');
          });
        })
        .catch(function (err) {
          releaseCamera();
          if (scanning) setScanMsg(cameraErrorCopy(err), 'error');
        });
    }

    if (cameraPossible) {
      scanBtn.hidden = false;
      Array.prototype.forEach.call(form.querySelectorAll('[data-oido-scan-hint]'), function (el) {
        el.hidden = false;
      });
      Array.prototype.forEach.call(
        form.querySelectorAll('[data-oido-scan-hint-alt]'),
        function (el) {
          el.hidden = true;
        }
      );
      scanBtn.addEventListener('click', function () {
        if (scanning) stopScan();
        else startScan();
      });
      var cameraTip = form.querySelector('[data-oido-camera-tip]');
      if (cameraTip) {
        cameraPermissionForgotten().then(function (forgotten) {
          if (!forgotten) return;
          cameraTip.textContent = cameraRememberTip();
          cameraTip.hidden = false;
        });
      }
      if (scanStopBtn) scanStopBtn.addEventListener('click', stopScan);
      // Never leave the camera light on behind a navigation or a backgrounded tab.
      window.addEventListener('pagehide', stopScan);
    }

    // ---- Identity -----------------------------------------------------------------------

    var identityFieldNames = clinicMode ? ['email', 'phone'] : ['firstName', 'lastName', 'email'];
    var identityAttempted = false;

    function identityField(name) {
      return form.querySelector('[name="' + name + '"]');
    }

    function updateIdentityValidity() {
      var emailEl = identityField('email');
      var phoneEl = identityField('phone');
      // Clear our previous custom errors first so checkValidity() below reflects
      // only the native email-format check, not a stale group error.
      if (emailEl) emailEl.setCustomValidity('');
      if (phoneEl) phoneEl.setCustomValidity('');
      var emailOk =
        !!emailEl &&
        getValue('email') !== '' &&
        (typeof emailEl.checkValidity !== 'function' || emailEl.checkValidity());
      if (clinicMode) {
        // erp-api takes a phone of 7 to 30 characters; a shorter one is a typo, not a contact.
        var phone = getValue('phone');
        if (phone !== '' && phone.length < 7 && phoneEl) {
          phoneEl.setCustomValidity('Enter the full phone number.');
        }
        var phoneOk = phone.length >= 7;
        if (!emailOk && !phoneOk && emailEl) emailEl.setCustomValidity(identityMessage);
        return emailOk || phoneOk;
      }
      var namesOk = getValue('firstName') !== '' && getValue('lastName') !== '';
      var ok = emailOk || namesOk;
      if (!ok && emailEl) emailEl.setCustomValidity(identityMessage);
      return ok;
    }

    // Clinic mode only: the address is optional, but a partial one is refused rather than lost.
    var addressRequiredNames = ['street1', 'city', 'state', 'zip'];
    var addressAttempted = false;

    function updateAddressValidity() {
      if (!clinicMode) return true;
      var empty = addressRequiredNames.filter(function (n) {
        return getValue(n) === '';
      });
      var ok = empty.length === 0 || empty.length === addressRequiredNames.length;
      addressRequiredNames.forEach(function (n) {
        var el = form.querySelector('[name="' + n + '"]');
        if (el) el.setCustomValidity(!ok && empty.indexOf(n) !== -1 ? addressMessage : '');
      });
      return ok;
    }

    if (clinicMode) {
      addressRequiredNames.forEach(function (n) {
        var el = form.querySelector('[name="' + n + '"]');
        if (!el) return;
        el.addEventListener('input', function () {
          if (!addressAttempted) return;
          if (updateAddressValidity() && statusEl && statusEl.textContent === addressMessage) {
            clearStatus();
          }
        });
      });
    }

    identityFieldNames.forEach(function (n) {
      var el = identityField(n);
      if (!el) return;
      el.addEventListener('input', function () {
        if (!identityAttempted) return;
        var ok = updateIdentityValidity();
        if (ok && statusEl && statusEl.textContent === identityMessage) clearStatus();
      });
    });

    // Collect the register payload, omitting empty optionals so the server's Zod
    // .optional() fields stay truly absent (an empty phone/street2 string would fail
    // validation or store a blank). Identity fields (email, first/last name) are
    // likewise sent only when non-empty; the either/or rule was already enforced
    // before submit.
    function collectPayload() {
      var data = {
        deviceSelection: getValue('deviceSelection'),
        productId: interpretProductId(getValue('productId')).productId,
        warrantyType: getValue('warrantyType'),
        purchaseDate: getValue('purchaseDate'),
      };
      if (clinicMode) data.location = getValue('location');
      else data.purchasedThroughAmazon = isChecked('purchasedThroughAmazon');
      var firstName = getValue('firstName');
      if (firstName) data.firstName = firstName;
      var lastName = getValue('lastName');
      if (lastName) data.lastName = lastName;
      var email = getValue('email');
      if (email) data.email = email;
      var phone = getValue('phone');
      if (phone) data.phone = phone;
      // The public form always sends the address (it is required there). A clinic sends it only
      // when it was filled in, and then whole: updateAddressValidity refused a partial one.
      if (!clinicMode || getValue('street1') !== '') {
        data.street1 = getValue('street1');
        data.city = getValue('city');
        data.state = getValue('state');
        data.zip = getValue('zip');
        var street2 = getValue('street2');
        if (street2) data.street2 = street2;
      }
      return data;
    }

    function getValue(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? String(el.value || '').trim() : '';
    }

    function isChecked(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return !!(el && el.checked);
    }

    // Customer-facing copy keyed by the server's machine-readable `code`.
    var ERROR_COPY = {
      invalid_product_id:
        'We could not verify that Product ID. Double-check the serial number or UDI ' +
        'printed on your device or its packaging and try again, or email help@oido.us.',
    };

    // Thrown when a clinic token is refused, so the submit handler sends the device back to
    // the sign-in step instead of showing an error on a form it can no longer use.
    var SIGNED_OUT = 'signed_out';

    function submitRegistration(payload) {
      var headers = { 'Content-Type': 'application/json' };
      var url = apiBase + '/v1/warranty/register';
      if (clinicMode) {
        url = apiBase + clinicPath + '/warranty/register';
        if (token) headers.Authorization = 'Bearer ' + token;
      }
      return fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      }).then(function (res) {
        if (res.status === 201) {
          return res.json();
        }
        if (clinicMode && res.status === 401) {
          throw new Error(SIGNED_OUT);
        }
        if (res.status === 429) {
          throw new Error('Too many attempts. Please wait a few minutes and try again.');
        }
        if (res.status === 400) {
          return res.json().then(
            function (body) {
              var msg =
                (body && body.code && ERROR_COPY[body.code]) ||
                (body && body.message) ||
                'Please check your details and try again.';
              throw new Error(msg);
            },
            function () {
              throw new Error('Please check your details and try again.');
            }
          );
        }
        throw new Error('Something went wrong. Please try again in a moment.');
      });
    }

    function onSuccess(result) {
      if (clinicMode && result && result.token) storeToken(result.token);
      form.reset();
      if (clinicMode) restoreClinicDefaults();
      var endDate = result && result.endDate ? result.endDate : null;
      var msg = clinicMode ? 'Sale registered.' : 'Your warranty is registered.';
      if (endDate) msg += ' Coverage runs through ' + endDate + '.';
      setStatus(msg, 'success');
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      stopScan();
      clearStatus();

      // A raw QR value typed by a HID scanner (which often ends in Enter, submitting the
      // form before any change event) is cleaned here first.
      if (productIdEl) {
        var interpreted = interpretProductId(productIdEl.value);
        if (interpreted.productId !== productIdEl.value || interpreted.model) {
          applyProductId(interpreted, false);
        }
      }

      // A required field inside a closed <details> cannot receive focus for the
      // native validation bubble, so open the address block first if any of its
      // required fields is invalid (in clinic mode, a half-entered address).
      addressAttempted = true;
      var addressOk = updateAddressValidity();
      if (addressDetails) {
        var addressInvalid = addressFieldNames.some(function (n) {
          var el = form.querySelector('[name="' + n + '"]');
          return el && typeof el.checkValidity === 'function' && !el.checkValidity();
        });
        if (addressInvalid) openAddress();
      }

      // Identity either/or group, then native validity (required fields,
      // email/date format, plus the custom identity error set just above).
      identityAttempted = true;
      productIdAttempted = true;
      updateProductIdValidity();
      var identityOk = updateIdentityValidity();
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        // Mirror the identity or address error into the live region: the native bubble is
        // transient and screen readers may miss it.
        if (!identityOk) setStatus(identityMessage, 'error');
        else if (!addressOk) setStatus(addressMessage, 'error');
        return;
      }
      // Fallback for browsers without reportValidity: never submit without identity.
      if (!identityOk || !addressOk) {
        setStatus(!identityOk ? identityMessage : addressMessage, 'error');
        return;
      }

      if (!apiBase) {
        setStatus('Registration is temporarily unavailable. Please try again later.', 'error');
        return;
      }

      setBusy(true);
      setStatus(clinicMode ? 'Registering the sale...' : 'Submitting your registration...', 'info');

      submitRegistration(collectPayload())
        .then(function (result) {
          onSuccess(result);
        })
        .catch(function (err) {
          if (err && err.message === SIGNED_OUT) {
            showGate('Your sign-in has expired. Enter the access code again.');
            return;
          }
          setStatus(
            isNetworkError(err)
              ? 'Could not reach OIDO. Check the connection and try again.'
              : (err && err.message) || 'Something went wrong. Please try again.',
            'error'
          );
        })
        .then(function () {
          setBusy(false);
        });
    });

    // ---- Clinic mode ----------------------------------------------------------------------

    var gate = root.querySelector('[data-oido-gate]');
    var gateStatus = gate ? gate.querySelector('[data-oido-gate-status]') : null;
    var gateBtn = gate ? gate.querySelector('[data-oido-gate-submit]') : null;
    var codeInput = gate ? gate.querySelector('[name="accessCode"]') : null;

    function storeToken(value) {
      token = value || null;
      writeStorage(TOKEN_KEY, token);
    }

    function setGateStatus(message, kind) {
      if (!gateStatus) return;
      gateStatus.hidden = !message;
      gateStatus.textContent = message || '';
      if (message) gateStatus.setAttribute('data-kind', kind || 'info');
    }

    function showGate(message) {
      stopScan();
      storeToken(null);
      form.hidden = true;
      if (!gate) return;
      gate.hidden = false;
      setGateStatus(message || '', 'error');
      if (codeInput) codeInput.focus();
    }

    function fillSelect(select, items, placeholderText) {
      while (select.options.length) select.remove(0);
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.textContent = placeholderText;
      select.appendChild(placeholder);
      items.forEach(function (item) {
        var opt = document.createElement('option');
        opt.value = item.value;
        opt.textContent = item.label;
        select.appendChild(opt);
      });
    }

    // Selects the clinic's default plan and this device's store. Run after the lists load
    // and after every successful registration (form.reset() puts both back on the
    // placeholder).
    function restoreClinicDefaults() {
      if (plansEl && plansEl.options.length > 1) plansEl.selectedIndex = 1;
      if (locationEl) {
        var remembered = readStorage(STORE_KEY);
        var known = Array.prototype.some.call(locationEl.options, function (o) {
          return o.value && o.value === remembered;
        });
        locationEl.value = known ? remembered : '';
      }
    }

    function applyClinicConfig(cfg) {
      if (cfg && cfg.token) storeToken(cfg.token);
      if (locationEl) {
        fillSelect(
          locationEl,
          (cfg.locations || []).map(function (name) {
            return { value: name, label: name };
          }),
          'Select your store'
        );
      }
      if (plansEl) fillSelect(plansEl, cfg.plans || [], 'Select the plan');
      restoreClinicDefaults();
    }

    function loadClinic() {
      if (!apiBase) {
        showGate('Registration is temporarily unavailable. Please try again later.');
        return;
      }
      fetch(apiBase + clinicPath + '/registration', {
        headers: { Authorization: 'Bearer ' + token },
      })
        .then(function (res) {
          if (res.status === 401) throw new Error(SIGNED_OUT);
          if (!res.ok) {
            return res.json().then(
              function (body) {
                throw new Error((body && body.message) || 'Registration is not available right now.');
              },
              function () {
                throw new Error('Registration is not available right now.');
              }
            );
          }
          return res.json();
        })
        .then(function (cfg) {
          applyClinicConfig(cfg);
          if (gate) gate.hidden = true;
          form.hidden = false;
        })
        .catch(function (err) {
          if (err && err.message === SIGNED_OUT) {
            showGate('Your sign-in has expired. Enter the access code again.');
            return;
          }
          // Keep the token: a network blip is not a reason to sign the device out.
          form.hidden = true;
          if (gate) gate.hidden = false;
          setGateStatus(
            isNetworkError(err)
              ? 'Could not reach OIDO. Check the connection and reload the page.'
              : (err && err.message) || 'Registration is not available right now.',
            'error'
          );
        });
    }

    if (clinicMode) {
      if (locationEl) {
        locationEl.addEventListener('change', function () {
          if (locationEl.value) writeStorage(STORE_KEY, locationEl.value);
        });
      }

      if (gate) {
        gate.addEventListener('submit', function (event) {
          event.preventDefault();
          var code = codeInput ? codeInput.value.trim() : '';
          if (!code) {
            setGateStatus('Enter the access code.', 'error');
            return;
          }
          if (!apiBase) {
            setGateStatus('Registration is temporarily unavailable. Please try again later.', 'error');
            return;
          }
          if (gateBtn) gateBtn.disabled = true;
          setGateStatus('Checking...', 'info');
          fetch(apiBase + clinicPath + '/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password: code }),
          })
            .then(function (res) {
              if (res.ok) return res.json();
              if (res.status === 503) {
                throw new Error(
                  'Registration is not set up for this clinic yet. Contact OIDO at help@oidohear.com.'
                );
              }
              return res.json().then(
                function (body) {
                  throw new Error((body && body.message) || 'That did not work. Try again.');
                },
                function () {
                  throw new Error('That did not work. Try again.');
                }
              );
            })
            .then(function (body) {
              storeToken(body.token);
              if (codeInput) codeInput.value = '';
              setGateStatus('', 'info');
              loadClinic();
            })
            .catch(function (err) {
              setGateStatus(
                isNetworkError(err)
                  ? 'Could not reach OIDO. Check the connection and try again.'
                  : (err && err.message) || 'That did not work. Try again.',
                'error'
              );
            })
            .then(function () {
              if (gateBtn) gateBtn.disabled = false;
            });
        });
      }

      if (token) loadClinic();
      else showGate('');
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-oido-warranty]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
